"use client";

import { useEffect, useRef } from "react";

const STATE_KEY = "rewebOverlay";
const owned = new Set<string>();
let count = 0;

/**
 * While the calling component is mounted it owns a history entry at the same address, so Back calls `onBack` (which
 * must unmount it) instead of leaving the page. Unmounting any other way takes the entry out again, so the next Back
 * goes to the previous page. An entry left behind by a reload while open is reused rather than stacked.
 */
export function useHistoryEntry(onBack: () => void) {
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const entry = useRef<string | null>(null);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    if (!entry.current) {
      const id = `${Date.now()}-${++count}`;
      const current = window.history.state?.[STATE_KEY];
      // The current state is copied so Next.js's router tree, and a page dialog the overlay sits in, stay with it.
      const state = { ...window.history.state, [STATE_KEY]: id };
      if (current && !owned.has(current))
        window.history.replaceState(state, "");
      else window.history.pushState(state, "");
      entry.current = id;
      owned.add(id);
    }
    const release = () => {
      if (entry.current) owned.delete(entry.current);
      entry.current = null;
    };
    const onPopState = () => {
      if (!entry.current || window.history.state?.[STATE_KEY] === entry.current)
        return;
      release();
      onBackRef.current();
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      mounted.current = false;
      window.removeEventListener("popstate", onPopState);
      // Strict Mode's rehearsal unmount is followed at once by a mount; only a real unmount leaves the entry.
      window.setTimeout(() => {
        if (mounted.current || !entry.current) return;
        const own = window.history.state?.[STATE_KEY] === entry.current;
        release();
        if (own) window.history.back();
      });
    };
  }, []);
}
